import { highestQualifyingAverage } from "./catalogAverageMatch";
import { namedSignupEntries } from "./extractSignupSheets";
import { readRawSheetBox } from "./sheetHighlight";
import {
  matchSignupRow,
  signupRowToCreateTeamBowler,
} from "./signupRowMatch";
import {
  isSinglesLikeEvent,
  isSignupPaidLabel,
  optionalEventLabel,
  printedOptionalTickLabels,
  printSinglesEventFromEvents,
} from "./signupSheets/signupSheetModel";
import {
  mergeSignupTickLabels,
  withNormalizedSignupTicks,
} from "./signupSheets/signupTicks";
import { printDoublesSinglesMode } from "./doublesSinglesSetup";
import {
  bowlerHighlightFromPairBox,
  pairHighlightFromNameBoxes,
  signupSheetHighlightBox,
  signupSheetPhotoHighlightBox,
  SIGNUP_SHEET_PHOTO_PAD,
  isUsableSheetPad,
  isSignupSheetFiducialToken,
} from "./signupSheets/signupSheetLayout";

export const emptyCreateTeamBowler = (index) => ({
  id: `temp-paper-${index}`,
  name: "",
  average: "",
  usbcID: "",
  email: "",
  phone: "",
  existingBowler: false,
  hasAverage: true,
  averagePopulated: false,
  selectedSidePots: {},
  bracketTickets: {},
  eventEntries: [],
  enterSingles: false,
  isLookupOpen: false,
  lookupResults: [],
  catalogSuggestions: [],
  catalogMatch: null,
  usbcConflict: false,
  averageDisagrees: false,
  errors: {},
});

const BLANK_LANE = /^(n\/?a|none|blank|null|-|—|–|lane)$/i;

export const laneNumberFromPad = (lane, { printedRowNumbers = 0 } = {}) => {
  const text = String(lane || "").trim();
  if (!text || BLANK_LANE.test(text) || /\bpage\b/i.test(text)) return "";
  const match = text.match(/^(?:lane\s*)?(\d{1,3})(?:\s*[-–]\s*\d{1,3})?$/i);
  if (!match) return "";
  const number = Number(match[1]);
  if (!Number.isInteger(number) || number < 1 || number > 128) return "";
  const copiedIndex =
    printedRowNumbers > 0 &&
    number <= printedRowNumbers &&
    !/[-–]/.test(text);
  if (copiedIndex) return "";
  return String(number);
};

export const normalizeTickLabel = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[.…]+$/g, "")
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const TICK_ALIASES = {
  hcp: "hdcp",
  hdcp: "hdcp",
  handicap: "hdcp",
  sgl: "singles",
  sg: "singles",
  single: "singles",
  singles: "singles",
  brk: "brackets",
  bracket: "brackets",
  brackets: "brackets",
  ae: "all event",
  "all event": "all event",
  "all events": "all event",
};

export const canonicalTickLabel = (value) => {
  const normalized = normalizeTickLabel(value);
  return TICK_ALIASES[normalized] || normalized;
};

export const tickLooksLikeSingles = (tick) => {
  if (canonicalTickLabel(tick) === "singles") return true;
  return normalizeTickLabel(tick)
    .split(" ")
    .some((token) => canonicalTickLabel(token) === "singles");
};

export const eventLooksLikeSingles = (event) => {
  if (!event) return false;
  if (isSinglesLikeEvent(event)) return true;
  return (
    canonicalTickLabel(event.eventName) === "singles" ||
    canonicalTickLabel(event.eventAbbreviation) === "singles" ||
    canonicalTickLabel(optionalEventLabel(event)) === "singles"
  );
};

export const expandTickLabels = (ticks) => {
  const labels = [];
  (ticks || []).forEach((tick) => {
    String(tick || "")
      .split(/[\s,;|/]+/)
      .forEach((part) => {
        const value = part.trim();
        if (value && !isSignupSheetFiducialToken(value) && !isSignupPaidLabel(value)) {
          labels.push(value);
        }
      });
  });
  return labels;
};

export const eventInitials = (event) =>
  normalizeTickLabel(event?.eventName)
    .split(" ")
    .map((part) => part[0])
    .join("");

export const tickMatchesEvent = (tick, event) => {
  const tickNorm = normalizeTickLabel(tick);
  const tickCanon = canonicalTickLabel(tick);
  if (!tickNorm) return false;
  const initials = eventInitials(event);
  const labels = [
    optionalEventLabel(event),
    event?.eventName,
    event?.eventAbbreviation,
    initials,
  ]
    .flatMap((value) => [normalizeTickLabel(value), canonicalTickLabel(value)])
    .filter(Boolean);
  if (labels.includes(tickNorm) || labels.includes(tickCanon)) return true;
  return labels.some((label) => {
    if (label.length < 3 || tickNorm.length < 3) return false;
    return label.includes(tickNorm) || tickNorm.includes(label);
  });
};

export const ticksToSelectedSidePots = (ticks, sidePotEvents) => {
  const selected = {};
  const labels = expandTickLabels(ticks)
    .filter((tick) => !tickLooksLikeSingles(tick));
  if (!labels.length) return selected;
  (sidePotEvents || []).forEach((event) => {
    if (eventLooksLikeSingles(event)) return;
    if (labels.some((label) => tickMatchesEvent(label, event))) {
      selected[event.id] = true;
    }
  });
  return selected;
};

const isTournamentWide = (event) =>
  event?.templateId == null || event?.templateId === "";

const isListedPrimary = (event) =>
  event?.isPrimary === true ||
  event?.isPrimary === "true" ||
  event?.isPrimary === 1;

export const findSinglesPrimaryEvent = (
  events,
  { excludeEventId, squadId } = {}
) => {
  const usable = (events || []).filter((event) => {
    if (!event?.id || event.isDeleted) return false;
    if (excludeEventId && event.id === excludeEventId) return false;
    return true;
  });
  const pick = (list) =>
    list.find((event) => eventLooksLikeSingles(event)) ||
    list.find(
      (event) => isListedPrimary(event) && Number(event.bowlerQuantity) === 1
    ) ||
    null;
  const tournamentWide = usable.filter(
    (event) => isTournamentWide(event) && event.isPrimary !== false
  );
  const sameSquad = usable.filter(
    (event) => event.squadId && squadId && event.squadId === squadId
  );
  return (
    pick(tournamentWide) ||
    pick(sameSquad) ||
    pick(usable.filter((event) => event.isPrimary !== false)) ||
    pick(usable)
  );
};

export const rowHasSinglesTick = (row) =>
  (row?.ticks || []).some((tick) => tickLooksLikeSingles(tick));

export const padSaysAlwaysSingles = (printedEvent) => {
  const text = normalizeTickLabel(printedEvent);
  if (!text) return false;
  return (
    /\bthen singles\b/.test(text) ||
    /\bthen sgl\b/.test(text) ||
    /\band singles\b/.test(text)
  );
};

export const rowEntersSingles = (row, singlesEvent) => {
  if (!row?.name) return false;
  if (rowHasSinglesTick(row)) return true;
  if (!singlesEvent) return false;
  return expandTickLabels(row.ticks).some((tick) =>
    tickMatchesEvent(tick, singlesEvent)
  );
};

export const signupRowEnterSingles = (row, sheet) => {
  if (!row?.name) return false;
  if (doublesPadImportsAllSingles(sheet)) return true;
  return rowEntersSingles(row);
};

export const doublesPadImportsAllSingles = (sheet) => {
  const extraction = sheet?.extraction || sheet?.sheet?.extraction || {};
  const labels = extraction.tickLabels || [];
  if (labels.some((label) => tickLooksLikeSingles(label))) return false;
  return (
    Boolean(extraction.alwaysSingles) ||
    padSaysAlwaysSingles(extraction.printedEvent)
  );
};

export const catalogHitToSuggestion = (hit, minGamesForAverage) => {
  const person = hit?.person || hit;
  if (!person?.id && !hit?.extractedBowlerID) return null;
  const candidate = highestQualifyingAverage(
    person.averages,
    minGamesForAverage
  );
  return {
    extractedBowlerID: person.id || hit.extractedBowlerID,
    name: person.name,
    usbcId: person.usbcId || person.usbcID,
    status: hit.status,
    reason: hit.reason,
    average: hit.average ?? candidate?.average ?? null,
    games: candidate?.games ?? null,
    source:
      candidate?.source || person.leagueName || person.fileName || "Catalog",
  };
};

const isPdfSheet = (sheet) =>
  String(sheet?.mimeType || "").includes("pdf") ||
  /\.pdf$/i.test(String(sheet?.fileName || ""));

const photoPadForReview = (sheet, entry) => {
  const page = Number.isInteger(entry?.sourcePage) ? entry.sourcePage : 0;
  const extracted = sheet?.extraction?.pads?.[page];
  return isUsableSheetPad(extracted) ? extracted : SIGNUP_SHEET_PHOTO_PAD;
};

const highlightBoxForReview = ({
  sheet,
  entry,
  rows,
  padTeamSize,
  slotIndex,
  bowlerIndex = null,
}) => {
  if (!isPdfSheet(sheet)) {
    const nameBoxes =
      bowlerIndex == null
        ? (rows || []).map((row) => row?.boundingBox)
        : [rows?.[0]?.boundingBox];
    const fromNames = pairHighlightFromNameBoxes(nameBoxes, {
      teamSize: padTeamSize,
      singleRow: bowlerIndex != null,
      includeLaneBar: padTeamSize >= 2 && bowlerIndex == null,
    });
    if (fromNames) {
      const pad = photoPadForReview(sheet, entry);
      if (
        padTeamSize >= 3 &&
        bowlerIndex == null &&
        isUsableSheetPad(pad)
      ) {
        return {
          ...fromNames,
          topLeftX: Math.min(fromNames.topLeftX, Number(pad.topLeftX) + 0.006),
          bottomRightX: Math.max(
            fromNames.bottomRightX,
            Number(pad.bottomRightX) - 0.006
          ),
        };
      }
      return fromNames;
    }
    return signupSheetPhotoHighlightBox({
      teamSize: padTeamSize,
      slotIndex,
      bowlerIndex,
      pad: photoPadForReview(sheet, entry),
    });
  }
  const stored = readRawSheetBox(entry?.boundingBox);
  if (stored && (bowlerIndex == null || bowlerIndex === "")) {
    return {
      ...stored,
      source: stored.source || entry?.boundingBox?.source || "layout",
    };
  }
  if (stored && bowlerIndex != null) {
    const fromPair = bowlerHighlightFromPairBox(stored, bowlerIndex, {
      teamSize: padTeamSize,
    });
    if (fromPair) return fromPair;
  }
  const nameBoxes =
    bowlerIndex == null
      ? (rows || []).map((row) => row?.boundingBox)
      : [rows?.[0]?.boundingBox];
  const fromNames = pairHighlightFromNameBoxes(nameBoxes, {
    teamSize: padTeamSize,
    singleRow: bowlerIndex != null,
  });
  if (fromNames) return fromNames;
  return signupSheetHighlightBox({
    teamSize: padTeamSize,
    slotIndex,
    bowlerIndex,
  });
};

export const signupSheetKey = (sheet) =>
  sheet?.importId || sheet?.storagePath || sheet?.fileName || "sheet";

const sheetRef = (sheet, entry) => ({
  importId: sheet.importId || null,
  consumedKeys: sheet.consumedKeys || [],
  storagePath: sheet.storagePath,
  mimeType: sheet.mimeType,
  fileName: sheet.fileName,
  sourcePage: entry.sourcePage,
  extraction: sheet.extraction
    ? {
        alwaysSingles: sheet.extraction.alwaysSingles,
        printedEvent: sheet.extraction.printedEvent,
        tickLabels: sheet.extraction.tickLabels,
      }
    : null,
});

const reviewEntry = ({
  key,
  sheet,
  entry,
  eventId,
  eventName,
  teamSize,
  teamName,
  lane,
  rows,
  highlightName,
  boundingBox,
}) => ({
  key,
  squadId: sheet.squadId,
  eventId,
  eventName,
  teamSize,
  teamName: teamName || "",
  lane,
  rows,
  highlightName: highlightName || "",
  boundingBox: boundingBox || null,
  sheet: sheetRef(sheet, entry),
});

export const serializeSignupReviewPlan = (entries) =>
  (entries || []).map((entry) => ({
    key: entry.key,
    eventId: entry.eventId,
    eventName: entry.eventName,
    teamSize: entry.teamSize,
    teamName: entry.teamName || "",
    lane: entry.lane || "",
    highlightName: entry.highlightName || "",
    boundingBox: entry.boundingBox || null,
    rows: entry.rows || [],
    sourcePage: entry.sheet?.sourcePage ?? 0,
  }));

export const hydrateSignupReviewPlan = (sheet, plan) =>
  (plan || []).map((entry) =>
    reviewEntry({
      key: entry.key,
      sheet,
      entry: { sourcePage: entry.sourcePage, boundingBox: entry.boundingBox },
      eventId: entry.eventId,
      eventName: entry.eventName,
      teamSize: entry.teamSize,
      teamName: entry.teamName || "",
      lane: entry.lane || "",
      rows: entry.rows || [],
      highlightName: entry.highlightName || "",
      boundingBox: entry.boundingBox || null,
    })
  );

export const tickLabelsForSignupSheet = (sheet, events, setup) => {
  const padTeamSize = Math.max(1, Number(sheet?.teamSize) || 1);
  const singlesEvent =
    padTeamSize === 2
      ? findSinglesPrimaryEvent(events, {
          excludeEventId: sheet?.eventId,
          squadId: sheet?.squadId,
        })
      : null;
  const tickLabels = mergeSignupTickLabels({
    storedLabels: sheet?.extraction?.tickLabels,
  });
  const fallbackLabels = tickLabels.length
    ? []
    : printedOptionalTickLabels({
        bowlerQuantity: padTeamSize,
        optionalEvents: (events || []).filter(
          (event) =>
            event && event.isDeleted !== true && event.isPrimary === false
        ),
        singlesEvent: printSinglesEventFromEvents(events),
        doublesSingles: printDoublesSinglesMode(setup),
      });
  return { padTeamSize, singlesEvent, tickLabels, fallbackLabels };
};

const deriveSignupReviewEntriesForSheet = (sheet, events, setup) => {
  const entries = [];
  const { padTeamSize, tickLabels, fallbackLabels } =
    tickLabelsForSignupSheet(sheet, events, setup);
  const filled = namedSignupEntries(sheet?.extraction, {
    teamSize: padTeamSize,
  }).map((entry) => {
    const pairNames = (entry.bowlers || []).map((row) => row?.name);
    return {
      ...entry,
      bowlers: (entry.bowlers || []).map((row) =>
        withNormalizedSignupTicks(row, tickLabels, pairNames, fallbackLabels)
      ),
    };
  });
  const parsedSlots = filled
    .map((entry) => entry.slotIndex)
    .filter((slot) => Number.isInteger(slot) && slot >= 0);
  const useParsedSlots =
    parsedSlots.length === filled.length &&
    new Set(parsedSlots).size === filled.length;
  const oneBasedSlots =
    useParsedSlots && parsedSlots.length > 0 && Math.min(...parsedSlots) === 1;
  const nextSlotByPage = {};
  filled.forEach((entry, index) => {
    const rows = entry.bowlers || [];
    const page = Number.isInteger(entry.sourcePage) ? entry.sourcePage : 0;
    const parsedSlot = Number(entry.slotIndex);
    const sequential = nextSlotByPage[page] ?? 0;
    const slotIndex =
      useParsedSlots && Number.isInteger(parsedSlot) && parsedSlot >= 0
        ? oneBasedSlots
          ? parsedSlot - 1
          : parsedSlot
        : sequential;
    nextSlotByPage[page] = sequential + 1;
    const laneConfirmed = entry.laneSource === "box";
    const sheetKey = sheet.storagePath || sheet.fileName || "sheet";
    entries.push(
      reviewEntry({
        key: `${sheetKey}-${index}`,
        sheet,
        entry,
        eventId: sheet.eventId,
        eventName: sheet.eventName,
        teamSize: padTeamSize,
        teamName: entry.teamName || "",
        lane: laneNumberFromPad(entry.lane, {
          printedRowNumbers: laneConfirmed ? 0 : padTeamSize,
        }),
        rows,
        highlightName: rows[0]?.name || "",
        boundingBox: highlightBoxForReview({
          sheet,
          entry,
          rows,
          padTeamSize,
          slotIndex,
        }),
      })
    );
  });
  return entries;
};

export const withSignupReviewPlan = (sheet, events, setup) => {
  if (!sheet) return sheet;
  const derived = deriveSignupReviewEntriesForSheet(sheet, events, setup);
  if (!derived.length) return sheet;
  return {
    ...sheet,
    extraction: {
      ...(sheet.extraction || {}),
      reviewPlan: serializeSignupReviewPlan(derived),
    },
  };
};

export const buildSignupReviewEntries = (sheets, { events, setup } = {}) => {
  const entries = [];
  (sheets || []).forEach((sheet) => {
    entries.push(...deriveSignupReviewEntriesForSheet(sheet, events, setup));
  });
  return entries;
};

export const pendingSignupReviewEntries = (sheets, options) =>
  buildSignupReviewEntries(sheets, options).filter(
    (entry) => !(entry.sheet?.consumedKeys || []).includes(entry.key)
  );

export const withConsumedSignupKey = (sheets, sheet, entryKey) =>
  (sheets || []).map((item) => {
    const sameImport = sheet?.importId && item.importId === sheet.importId;
    const samePath =
      !sheet?.importId &&
      item.storagePath &&
      item.storagePath === sheet.storagePath;
    if (!sameImport && !samePath) return item;
    return {
      ...item,
      consumedKeys: Array.from(
        new Set([...(item.consumedKeys || []), entryKey].filter(Boolean))
      ),
    };
  });

export const mergeSignupSheets = (current, incoming) => {
  const byKey = new Map();
  [...(current || []), ...(incoming || [])].forEach((sheet) => {
    const key = signupSheetKey(sheet);
    if (!key || key === "sheet") return;
    byKey.set(key, sheet);
  });
  return Array.from(byKey.values());
};


const mergeOrgBowlers = (existingBowlers, tournamentBowlers) => {
  const bowlerMap = new Map();
  (existingBowlers || []).forEach((bowler) => {
    if (bowler?.id) bowlerMap.set(bowler.id, bowler);
  });
  (tournamentBowlers || []).forEach((bowler) => {
    if (bowler?.id) bowlerMap.set(bowler.id, bowler);
  });
  return Array.from(bowlerMap.values());
};

export const reviewBowlersFromEntry = ({
  entry,
  teamSize,
  existingBowlers = [],
  tournamentBowlers = [],
  catalogPeople = [],
  sidePotEvents = [],
  minGamesForAverage = 21,
  alwaysEnterSingles = false,
}) => {
  const orgBowlers = mergeOrgBowlers(existingBowlers, tournamentBowlers);
  const quantity = Math.max(
    1,
    Number(teamSize) || Number(entry?.teamSize) || entry?.rows?.length || 1
  );
  const excludeOrgIds = [];
  const excludeCatalogIds = [];
  return Array.from({ length: quantity }, (_, index) => {
    const row = entry?.rows?.[index];
    if (!row?.name) return emptyCreateTeamBowler(index);
    const match = matchSignupRow({
      name: row.name,
      usbcID: row.usbcId || row.usbcID,
      email: row.email,
      phone: row.phone,
      writtenAverage: row.average,
      orgBowlers,
      catalogPeople,
      minGamesForAverage,
      excludeOrgIds,
      excludeCatalogIds,
    });
    if (match.org.auto?.id) excludeOrgIds.push(match.org.auto.id);
    if (match.catalog.auto?.id) excludeCatalogIds.push(match.catalog.auto.id);

    const payload = signupRowToCreateTeamBowler({
      name: row.name,
      usbcID: row.usbcId || row.usbcID,
      email: row.email,
      phone: row.phone,
      writtenAverage: row.average,
      ticks: ticksToSelectedSidePots(row.ticks, sidePotEvents),
      match,
    });
    const catalogSuggestions = (match.catalog.suggestions || [])
      .map((hit) => catalogHitToSuggestion(hit, minGamesForAverage))
      .filter(Boolean);
    const catalogMatch = payload.catalogMatch || null;
    const orgLookups = (match.org.suggestions || [])
      .filter((hit) => hit.status !== "autoMatched")
      .map((hit) => hit.person)
      .filter(Boolean);

    return {
      ...emptyCreateTeamBowler(index),
      ...payload,
      id: payload.id || `temp-paper-${index}`,
      catalogMatch,
      catalogSuggestions: catalogMatch ? [] : catalogSuggestions,
      lookupResults: payload.existingBowler ? [] : orgLookups.slice(0, 10),
      averagePopulated: Boolean(
        payload.hasAverage &&
          (row.average == null || row.average === "") &&
          catalogMatch
      ),
      previousAverage: payload.average,
      previousHasAverage: payload.hasAverage,
      enterSingles:
        Number(quantity) === 2 &&
        (Boolean(alwaysEnterSingles) || signupRowEnterSingles(row, entry)),
    };
  });
};
